/**
 * Storage utility with Safari private browsing mode support.
 *
 * Safari private browsing mode throws QuotaExceededError when trying to
 * write to localStorage, even for the first item. This utility provides
 * safe wrappers that gracefully handle these errors.
 *
 * Cross-browser compatibility:
 * - Chrome: Full support
 * - Firefox: Full support
 * - Safari: Full support (with private mode handling)
 * - iOS Safari: Full support (with private mode handling)
 * - Edge: Full support
 */

// In-memory fallback for when localStorage is unavailable
const memoryStorage: Map<string, string> = new Map();

// Track if localStorage is available (cached for performance)
let localStorageAvailable: boolean | null = null;

/**
 * Check if localStorage is available and writable.
 * Caches the result for performance.
 */
export function isLocalStorageAvailable(): boolean {
  if (localStorageAvailable !== null) {
    return localStorageAvailable;
  }

  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    localStorageAvailable = true;
    return true;
  } catch (_e) {
    localStorageAvailable = false;
    console.warn(
      '[Storage] localStorage unavailable (private browsing mode?), using memory fallback',
    );
    return false;
  }
}

/**
 * Safely get an item from storage.
 * Falls back to memory storage if localStorage is unavailable.
 *
 * @param key - The storage key
 * @returns The stored value or null if not found
 */
export function safeGetItem(key: string): string | null {
  try {
    if (isLocalStorageAvailable()) {
      return localStorage.getItem(key);
    }
    return memoryStorage.get(key) ?? null;
  } catch (_e) {
    // Fallback to memory storage on any error
    return memoryStorage.get(key) ?? null;
  }
}

/**
 * Safely set an item in storage.
 * Falls back to memory storage if localStorage is unavailable or quota exceeded.
 *
 * @param key - The storage key
 * @param value - The value to store
 * @returns true if stored successfully, false otherwise
 */
export function safeSetItem(key: string, value: string): boolean {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.setItem(key, value);
      return true;
    }
    memoryStorage.set(key, value);
    return true;
  } catch (_e) {
    // QuotaExceededError or other storage error
    // Fall back to memory storage
    memoryStorage.set(key, value);
    console.warn(
      `[Storage] Failed to write to localStorage for key "${key}", using memory fallback`,
    );
    return false;
  }
}

/**
 * Safely remove an item from storage.
 *
 * @param key - The storage key to remove
 * @returns true if removed successfully
 */
export function safeRemoveItem(key: string): boolean {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.removeItem(key);
    }
    memoryStorage.delete(key);
    return true;
  } catch (_e) {
    memoryStorage.delete(key);
    return false;
  }
}

/**
 * Safely clear all storage.
 *
 * @returns true if cleared successfully
 */
export function safeClear(): boolean {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.clear();
    }
    memoryStorage.clear();
    return true;
  } catch (_e) {
    memoryStorage.clear();
    return false;
  }
}

/**
 * Get a JSON value from storage with type safety.
 *
 * @param key - The storage key
 * @param defaultValue - Default value if key doesn't exist or parse fails
 * @returns The parsed value or default
 */
export function safeGetJSON<T>(key: string, defaultValue: T): T {
  const value = safeGetItem(key);
  if (value === null) {
    return defaultValue;
  }
  try {
    return JSON.parse(value) as T;
  } catch (_e) {
    console.warn(`[Storage] Failed to parse JSON for key "${key}"`);
    return defaultValue;
  }
}

/**
 * Set a JSON value in storage.
 *
 * @param key - The storage key
 * @param value - The value to serialize and store
 * @returns true if stored successfully
 */
export function safeSetJSON<T>(key: string, value: T): boolean {
  try {
    return safeSetItem(key, JSON.stringify(value));
  } catch (_e) {
    console.warn(`[Storage] Failed to stringify value for key "${key}"`);
    return false;
  }
}

// SessionStorage wrappers (same pattern)
let sessionStorageAvailable: boolean | null = null;
const memorySessionStorage: Map<string, string> = new Map();

/**
 * Check if sessionStorage is available and writable.
 */
export function isSessionStorageAvailable(): boolean {
  if (sessionStorageAvailable !== null) {
    return sessionStorageAvailable;
  }

  try {
    const testKey = '__session_test__';
    sessionStorage.setItem(testKey, testKey);
    sessionStorage.removeItem(testKey);
    sessionStorageAvailable = true;
    return true;
  } catch (_e) {
    sessionStorageAvailable = false;
    return false;
  }
}

/**
 * Safely get an item from sessionStorage.
 */
export function safeSessionGetItem(key: string): string | null {
  try {
    if (isSessionStorageAvailable()) {
      return sessionStorage.getItem(key);
    }
    return memorySessionStorage.get(key) ?? null;
  } catch (_e) {
    return memorySessionStorage.get(key) ?? null;
  }
}

/**
 * Safely set an item in sessionStorage.
 */
export function safeSessionSetItem(key: string, value: string): boolean {
  try {
    if (isSessionStorageAvailable()) {
      sessionStorage.setItem(key, value);
      return true;
    }
    memorySessionStorage.set(key, value);
    return true;
  } catch (_e) {
    memorySessionStorage.set(key, value);
    return false;
  }
}

/**
 * Safely remove an item from sessionStorage.
 */
export function safeSessionRemoveItem(key: string): boolean {
  try {
    if (isSessionStorageAvailable()) {
      sessionStorage.removeItem(key);
    }
    memorySessionStorage.delete(key);
    return true;
  } catch (_e) {
    memorySessionStorage.delete(key);
    return false;
  }
}

/**
 * Get a JSON value from sessionStorage with type safety.
 */
export function safeSessionGetJSON<T>(key: string, defaultValue: T): T {
  const value = safeSessionGetItem(key);
  if (value === null) {
    return defaultValue;
  }
  try {
    return JSON.parse(value) as T;
  } catch (_e) {
    return defaultValue;
  }
}

/**
 * Set a JSON value in sessionStorage.
 */
export function safeSessionSetJSON<T>(key: string, value: T): boolean {
  try {
    return safeSessionSetItem(key, JSON.stringify(value));
  } catch (_e) {
    return false;
  }
}
